import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { evictPostsFromFeedCache as evictFromFeedCache } from '../../api/feedCacheHelpers';
import {
  matchFeedPostByAuthor,
  matchFeedPostById,
  matchFeedPostByThread,
} from '../../api/feedCacheMatchers';
import FeedPostCard from '../../components/FeedPostCard';
import ReportPostDialog from '../../components/ReportPost/ReportPostDialog';
import ShareStoryModal from '../../components/ShareStory/ShareStoryModal';
import TextEditor from '../../components/TextEditor';
import ErrorMessages from '../../constants/error-messages';
import { FEED_CONSTANTS } from '../../constants/feed.constants';
import { useAuth } from '../../context/AuthContext';
import {
  useRecommendations,
  useSocket,
  useWebSocketStatus,
} from '../../context/WebsocketContext';
import type { GetUserFeedQueryVariables } from '../../gql/graphql';
import { HIDE_POST_FROM_FEED } from '../../graphql/resolvers/feed/feedMutations/hidePostFromFeed.gql';
import { SUBMIT_FEEDBACK_SIGNAL } from '../../graphql/resolvers/feed/feedMutations/submitFeedbackSignal.gql';
import { TOGGLE_ACCOUNT_BLOCK } from '../../graphql/resolvers/feed/feedMutations/toggleAccountBlock.gql';
import { TOGGLE_AUTHOR_MUTE } from '../../graphql/resolvers/feed/feedMutations/toggleAuthorMute.gql';
import { TOGGLE_THREAD_MUTE } from '../../graphql/resolvers/feed/feedMutations/toggleThreadMute.gql';
import { GET_USER_FEED } from '../../graphql/resolvers/feed/feedQueries/getUserFeed';
import { useFeedPostSubscriptions } from '../../hooks/useFeedPostSubscriptions';
import { usePostInteractions } from '../../hooks/usePostInteractions';
import { useStoryScoreUpdates } from '../../hooks/useStoryScoreUpdates';
import type { FeedPost } from '../../interfaces/feed.interface';
import type { PostReplyDraftInterfaceState } from '../../interfaces/post.interface';
import { draftSlice } from '../../store/draft.store';
import { FeedEntryType } from '../../types/FeedEntryType.type';
import { buildFeedVariables as buildFeedVariablesFn } from './buildFeedVariables';
import './recommendation-feed.scss';

interface RecommendationFeedProps {
  className?: string;
  initialFeedType?: FeedEntryType;
  feedType?: FeedEntryType;
}

const normalizePostsForUser = (
  posts: FeedPost[],
  userId?: string,
  username?: string,
) =>
  posts.map((post) => {
    const authorId =
      (post.author as any)?.authorId ??
      (post.author as any)?.userId ??
      post.userId ??
      null;

    const isOwnPost =
      (!!authorId && userId && authorId === userId) ||
      (!!username && post.author?.username === username);

    const viewerInteractions = {
      hasLiked: post.viewerInteractions?.hasLiked ?? false,
      hasReposted: post.viewerInteractions?.hasReposted ?? false,
      hasBookmarked: post.viewerInteractions?.hasBookmarked ?? false,
      likeCount: post.viewerInteractions?.likeCount ?? 0,
      bookmarkCount: post.viewerInteractions?.bookmarkCount ?? 0,
    };

    return {
      ...post,
      viewerInteractions,
      isFollowingAuthor: isOwnPost
        ? false
        : (post.isFollowingAuthor ??
          post.feedMetadata?.entryType === FeedEntryType.FOLLOWING),
    };
  });

const RecommendationFeed: React.FC<RecommendationFeedProps> = ({
  className = '',
  feedType,
}) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const topicFilter = searchParams.get('topic') || undefined;
  const { username } = params;
  const { IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION, FEED_POST_ID } =
    draftSlice.actions;
  const { getScoreForStory } = useStoryScoreUpdates();
  const { seedScores } = useSocket();
  const { connected, authenticated } = useWebSocketStatus();

  // Seed the WS score map from post.score — the server now derives and
  // persists the correct score at publish time (TRIL-102), so post.score
  // is the single source of truth. No storyAnalysis fallback needed.
  const collectInitialScores = useCallback(
    (posts: Array<Record<string, any>>) =>
      posts
        .map((p) => {
          const storyId = (p.postId ?? p.id) as string | undefined;
          const score = p?.score;
          return storyId && typeof score === 'number'
            ? { storyId, score }
            : null;
        })
        .filter((e): e is { storyId: string; score: number } => e !== null),
    [],
  );
  const { unreadCount, clearRecommendations } = useRecommendations();
  const { toggleLike, toggleBookmark } = usePostInteractions();

  // TRIL-45 — ellipsis menu state + toast.
  const [reportDialogTril45, setReportDialogTril45] = useState<{
    open: boolean;
    postId: string;
  }>({ open: false, postId: '' });
  const [actionToastTril45, setActionToastTril45] = useState<{
    message: string;
    variant: 'success' | 'error';
  } | null>(null);
  const actionToastTimeoutRefTril45 = useRef<number | null>(null);

  const showActionToast = useCallback(
    (message: string, variant: 'success' | 'error' = 'success') => {
      setActionToastTril45({ message, variant });
      if (actionToastTimeoutRefTril45.current) {
        clearTimeout(actionToastTimeoutRefTril45.current);
      }
      actionToastTimeoutRefTril45.current = window.setTimeout(() => {
        setActionToastTril45(null);
        actionToastTimeoutRefTril45.current = null;
      }, 2600);
    },
    [],
  );

  // TRIL-45 — ellipsis menu mutations.
  // Apollo cache eviction helper. Since TRIL-177 the feed renders from
  // the getUserFeed cached query result; since TRIL-179 cache.modify is
  // the sole removal path (the redundant Redux dispatch was dropped).
  // Without this, the cached posts array still contains the removed
  // post, so the next remount/refocus re-renders the ghost.
  const apolloClient = useApolloClient();
  const evictPostsFromFeedCache = useCallback(
    (shouldRemove: (post: Record<string, any>) => boolean) => {
      evictFromFeedCache(apolloClient.cache, shouldRemove);
    },
    [apolloClient],
  );
  const [hidePostFromFeedMutation] = useMutation(HIDE_POST_FROM_FEED);
  const [toggleThreadMuteMutation] = useMutation(TOGGLE_THREAD_MUTE);
  const [toggleAccountBlockMutation] = useMutation(TOGGLE_ACCOUNT_BLOCK);
  const [submitFeedbackSignalMutation] = useMutation(SUBMIT_FEEDBACK_SIGNAL);
  const [toggleAuthorMuteMutation] = useMutation(TOGGLE_AUTHOR_MUTE);

  const handleCopyLink = useCallback(
    async (postId: string) => {
      const url = `${window.location.origin}/post/${postId}`;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          showActionToast('Link copied');
        } else {
          showActionToast('Clipboard unavailable', 'error');
        }
      } catch {
        showActionToast('Could not copy link', 'error');
      }
    },
    [showActionToast],
  );

  const handleShowMoreLikeThis = useCallback(
    async (postId: string) => {
      try {
        await submitFeedbackSignalMutation({
          variables: { postId, signal: 'MORE_LIKE' },
        });
        showActionToast("We'll show you more like this");
      } catch (err) {
        console.error('submitFeedbackSignal (MORE_LIKE) failed', err);
        showActionToast('Could not send feedback', 'error');
      }
    },
    [submitFeedbackSignalMutation, showActionToast],
  );

  const handleShowLessLikeThis = useCallback(
    async (postId: string) => {
      try {
        // Sequence: hide first (user-visible effect), then fire the
        // ranker signal. If the signal fails we still remove the post
        // and surface a soft warning — the hide succeeded so the UI
        // state matches the server. If the hide fails we bail before
        // evicting from cache, and the signal is never sent.
        await hidePostFromFeedMutation({ variables: { postId } });
        evictPostsFromFeedCache(matchFeedPostById(postId));
        try {
          await submitFeedbackSignalMutation({
            variables: { postId, signal: 'LESS_LIKE' },
          });
          showActionToast("We'll show less like this");
        } catch (signalErr) {
          console.error('submitFeedbackSignal (LESS_LIKE) failed', signalErr);
          showActionToast('Hidden — feedback signal failed', 'error');
        }
      } catch (err) {
        console.error('show less like this failed', err);
        showActionToast('Could not update preferences', 'error');
      }
    },
    [
      submitFeedbackSignalMutation,
      hidePostFromFeedMutation,
      evictPostsFromFeedCache,
      showActionToast,
    ],
  );

  const handleMuteThread = useCallback(
    async (post: FeedPost) => {
      const rootPostId = (post as any).rootPostId ?? post.postId ?? post.id;
      if (!rootPostId) return;
      try {
        await toggleThreadMuteMutation({ variables: { rootPostId } });
        evictPostsFromFeedCache(matchFeedPostByThread(rootPostId));
        showActionToast('Thread muted');
      } catch (err) {
        console.error('toggleThreadMute failed', err);
        showActionToast('Could not mute thread', 'error');
      }
    },
    [toggleThreadMuteMutation, evictPostsFromFeedCache, showActionToast],
  );

  const handleHidePost = useCallback(
    async (postId: string) => {
      try {
        await hidePostFromFeedMutation({ variables: { postId } });
        evictPostsFromFeedCache(matchFeedPostById(postId));
        showActionToast('Post hidden');
      } catch (err) {
        console.error('hidePostFromFeed failed', err);
        showActionToast('Could not hide post', 'error');
      }
    },
    [hidePostFromFeedMutation, evictPostsFromFeedCache, showActionToast],
  );

  const handleMuteAccount = useCallback(
    async (authorId: string | null) => {
      if (!authorId) return;
      try {
        await toggleAuthorMuteMutation({
          variables: { authorId },
        });
        evictPostsFromFeedCache(matchFeedPostByAuthor(authorId));
        showActionToast('Account muted');
      } catch (err) {
        console.error('toggleAuthorMute failed', err);
        showActionToast('Could not mute account', 'error');
      }
    },
    [toggleAuthorMuteMutation, evictPostsFromFeedCache, showActionToast],
  );

  const handleBlockAccount = useCallback(
    async (authorId: string | null) => {
      if (!authorId) return;
      try {
        await toggleAccountBlockMutation({
          variables: { accountId: authorId },
        });
        evictPostsFromFeedCache(matchFeedPostByAuthor(authorId));
        showActionToast('Account blocked');
      } catch (err) {
        console.error('toggleAccountBlock failed', err);
        showActionToast('Could not block account', 'error');
      }
    },
    [toggleAccountBlockMutation, evictPostsFromFeedCache, showActionToast],
  );

  const handleOpenReport = useCallback((postId: string) => {
    setReportDialogTril45({ open: true, postId });
  }, []);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [shareModal, setShareModal] = useState<{
    open: boolean;
    storyId: string;
    storyTitle: string;
    storyUrl: string;
  }>({ open: false, storyId: '', storyTitle: '', storyUrl: '' });
  const toastTimeoutRef = useRef<number | null>(null);
  const { isEditingPostReplyDraft } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );

  // Initial query variables — same helper as pagination / refresh /
  // reconnect so feedType + topic can't drift from the other call sites.
  const variables = buildFeedVariablesFn({
    userId: user?.id,
    cursor: null,
    limit: 20,
    feedType,
    topic: topicFilter,
  });

  const { data, loading, error, refetch, fetchMore } = useQuery(GET_USER_FEED, {
    // TRIL-176 Slice 1: transitional — buildFeedVariables yields userId: string | undefined,
    // but the generated type requires string; the skip guard ensures it's defined at fire time.
    // A later slice aligns buildFeedVariables' return type with GetUserFeedQueryVariables.
    variables: variables as unknown as GetUserFeedQueryVariables,
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // TRIL-177: the feed list is rendered from the Apollo cache (getUserFeed
  // field policy). The `as unknown as FeedPost[]` boundary cast stays (TRIL-176).
  const feedPosts = useMemo(
    () =>
      normalizePostsForUser(
        (data?.getUserFeed?.posts ?? []) as unknown as FeedPost[],
        user?.id,
        user?.username,
      ),
    [data?.getUserFeed?.posts, user?.id, user?.username],
  );

  const visiblePostIds = feedPosts
    .map((p) => (p.postId ?? p.id) as string | undefined)
    .filter((id): id is string => !!id);
  useFeedPostSubscriptions(visiblePostIds);

  // TRIL-189: the route-scoped prevPathRef effect that used to live here
  // was structurally broken (useRef captured the mount-time path, not the
  // prior route, because RecommendationFeed unmounts on /post/X and re-
  // mounts on return to feed). Cross-route eviction for /post/X → feed
  // is now handled in App.tsx where the useRef persists across all routes.

  const handleAddPostReplyDraft = (id: string) => {
    setIsReplyModalOpen(true);
    dispatch(IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION(true));
    dispatch(FEED_POST_ID(id));
  };

  const handleShareModal = (
    postId: string,
    postTitle: string,
    post: FeedPost,
  ) => {
    setShareModal({
      open: true,
      storyId: postId,
      storyTitle: postTitle,
      storyUrl: `/post/${postId}`,
    });
  };

  const handleToggleLike = useCallback(
    (post: FeedPost) => {
      void toggleLike(post);
    },
    [toggleLike],
  );

  const handleToggleBookmark = useCallback(
    (post: FeedPost) => {
      void toggleBookmark(post);
    },
    [toggleBookmark],
  );

  useEffect(() => {
    if (data?.getUserFeed?.posts) {
      const posts = normalizePostsForUser(
        data.getUserFeed.posts as unknown as FeedPost[],
        user?.id,
        user?.username,
      );

      seedScores(collectInitialScores(posts));
    }
  }, [
    data,
    dispatch,
    user?.id,
    user?.username,
    seedScores,
    collectInitialScores,
  ]);

  // Wraps the pure buildFeedVariables helper with the current
  // userId / feedType / topicFilter so every call site (initial query,
  // pagination, refresh, reconnect reconcile) shares one variable shape.
  // Lock-in tests live in pages/Feed/__tests__/buildFeedVariables.test.ts.
  const buildFeedVariables = useCallback(
    (overrides: { cursor: string | null; limit: number }) =>
      buildFeedVariablesFn({
        userId: user?.id,
        cursor: overrides.cursor,
        limit: overrides.limit,
        feedType,
        topic: topicFilter,
      }),
    [user?.id, feedType, topicFilter],
  );

  const loadMorePosts = useCallback(async () => {
    const endCursor = data?.getUserFeed?.pageInfo?.endCursor;
    if (!endCursor || loading) return;

    try {
      // fetchMore merges the next page into the cache via the getUserFeed field
      // policy (drives the display).
      const result = await fetchMore({
        variables: buildFeedVariables({ cursor: endCursor, limit: 20 }),
      });

      if (result.data?.getUserFeed?.posts) {
        const newPosts = normalizePostsForUser(
          result.data.getUserFeed.posts as unknown as FeedPost[],
          user?.id,
          user?.username,
        );

        seedScores(collectInitialScores(newPosts));
      }
    } catch (error) {
      console.error(`${ErrorMessages.FEED.LOADING}`, error);
    }
  }, [
    data?.getUserFeed?.pageInfo?.endCursor,
    loading,
    fetchMore,
    buildFeedVariables,
    user?.id,
    user?.username,
    dispatch,
    seedScores,
    collectInitialScores,
  ]);

  const refreshFeed = useCallback(async () => {
    try {
      clearRecommendations();

      // Evict the cached getUserFeed list so the head refetch replaces it
      // cleanly (merge case 3) rather than reconciling onto the old pages.
      // Evicts all keyArgs buckets (every feedType/topic) — acceptable: one
      // user per client session.
      apolloClient.cache.evict({ fieldName: 'getUserFeed' });
      apolloClient.cache.gc();

      const result = await refetch(
        buildFeedVariables({ cursor: null, limit: 20 }),
      );

      if (result.data?.getUserFeed?.posts) {
        const posts = normalizePostsForUser(
          result.data.getUserFeed.posts as unknown as FeedPost[],
          user?.id,
          user?.username,
        );

        seedScores(collectInitialScores(posts));
      }
    } catch (error) {
      console.error(`${ErrorMessages.FEED.REFRESHING}`, error);
    }
  }, [
    apolloClient,
    refetch,
    clearRecommendations,
    buildFeedVariables,
    user?.id,
    user?.username,
    dispatch,
    seedScores,
    collectInitialScores,
  ]);

  useEffect(() => {
    if (!connected || !authenticated) return;
    if (!unreadCount || unreadCount <= 0) return;

    (async () => {
      try {
        await refreshFeed();

        setShowToast(true);

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }
        toastTimeoutRef.current = window.setTimeout(() => {
          setShowToast(false);
          toastTimeoutRef.current = null;
        }, 3000);
      } catch (err) {
        console.error('Error auto-refreshing feed after WS notification', err);
      } finally {
        clearRecommendations();
      }
    })();
  }, [
    unreadCount,
    connected,
    authenticated,
    refreshFeed,
    clearRecommendations,
  ]);

  // Reconnect reconciliation for the feed. If the websocket bounced
  // (network blip, ALB idle close, etc.) any INTERACTION_UPDATED /
  // new_replies / STORY_SCORE_UPDATED that fired during the gap is
  // permanently lost to this client. We refetch the head page on
  // reconnect — the getUserFeed Apollo field policy's reconcile case
  // (TRIL-177 case 2) updates any post in the head fetch in-place and
  // prepends genuine newcomers, but explicitly does NOT drop later-page
  // posts the user has already paginated to. seedScores re-warms the WS
  // score map. (Distinct from refreshFeed, which is user-initiated and
  // forces a clean replace via cache.evict.)
  const reconcileFeedFromServer = useCallback(async () => {
    try {
      const result = await refetch(
        buildFeedVariables({ cursor: null, limit: 20 }),
      );
      if (!result.data?.getUserFeed?.posts) return;
      const posts = normalizePostsForUser(
        result.data.getUserFeed.posts as unknown as FeedPost[],
        user?.id,
        user?.username,
      );
      seedScores(collectInitialScores(posts));
    } catch (error) {
      console.error('Feed reconnect reconcile failed', error);
    }
  }, [
    refetch,
    buildFeedVariables,
    user?.id,
    user?.username,
    dispatch,
    seedScores,
    collectInitialScores,
  ]);

  const wasAuthenticatedRef = useRef(authenticated);
  useEffect(() => {
    const wasAuth = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = authenticated;
    if (wasAuth === false && authenticated === true) {
      void reconcileFeedFromServer();
    }
  }, [authenticated, reconcileFeedFromServer]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      if (actionToastTimeoutRefTril45.current) {
        clearTimeout(actionToastTimeoutRefTril45.current);
      }
    };
  }, []);

  if (!user) {
    return (
      <section className="recommendation-feed">
        <p>Please log in to see your personalized recommendations.</p>
      </section>
    );
  }

  if (loading && feedPosts.length === 0) {
    return (
      <section className="recommendation-feed">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{FEED_CONSTANTS.LOADING.LOADING_FEED}</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="recommendation-feed">
        <div className="error-container">
          <p>Error loading your feed: {error.message}</p>
          <button type="button" onClick={refreshFeed} className="retry-button">
            Try Again
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={`USER__FEED__PAGE ${className}`}>
        <div className="feed-header">
          <h2>Recommended For You</h2>
          {actionToastTril45 && (
            <div
              className={`feed-toast feed-toast--action feed-toast--${actionToastTril45.variant}`}
              role={actionToastTril45.variant === 'error' ? 'alert' : 'status'}
            >
              <span className="feed-toast__icon" aria-hidden="true">
                {actionToastTril45.variant === 'success' ? '✓' : '✕'}
              </span>
              <span className="feed-toast__text">
                {actionToastTril45.message}
              </span>
            </div>
          )}
          <div className="connection-status">
            <div
              className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}
            >
              <span className="status-dot"></span>
              {connected
                ? authenticated
                  ? 'Live'
                  : 'Connecting...'
                : 'Offline'}
            </div>
          </div>
        </div>

        {showToast && (
          <div className="feed-toast">
            <span className="feed-toast__icon">🔄</span>
            <span className="feed-toast__text">Feed updated</span>
          </div>
        )}

        {feedPosts.length === 0 ? (
          <div className="empty-feed">
            <div className="empty-feed-content">
              <h3>{FEED_CONSTANTS.NO_RECOMMENDATIONS}</h3>
              <p>
                We're learning about your interests. Check back soon for
                personalized content!
              </p>
              <button
                type="button"
                onClick={refreshFeed}
                className="refresh-button"
              >
                Refresh Feed
              </button>
            </div>
          </div>
        ) : (
          <div className={'USER__FEED'}>
            {feedPosts.map((post, index) => {
              const postId = post.postId ?? post.id;

              return (
                <FeedPostCard
                  key={`${postId}-${post.feedMetadata?.addedToFeedAt ?? index}`}
                  post={post}
                  userId={user.id}
                  username={user.username}
                  onToggleLike={handleToggleLike}
                  onToggleBookmark={handleToggleBookmark}
                  onReply={handleAddPostReplyDraft}
                  onCopyLink={handleCopyLink}
                  onOpenShare={handleShareModal}
                  onMuteThread={handleMuteThread}
                  onMuteAccount={handleMuteAccount}
                  onBlockAccount={handleBlockAccount}
                  onOpenReport={handleOpenReport}
                  onShowMoreLikeThis={handleShowMoreLikeThis}
                  onShowLessLikeThis={handleShowLessLikeThis}
                  onHidePost={handleHidePost}
                  scoreUpdate={getScoreForStory(postId)}
                  showScore
                  showCategories
                />
              );
            })}

            {/*
              TRIL-218: Single page-level TextEditor. Pre-fix this was mounted
              inside the .map() callback above (one per card), so opening the
              reply modal stacked N copies of Dialog.Portal — each rendering a
              .SimpleDialog__Overlay with rgba(0,0,0,0.5), producing an
              effectively opaque black backdrop (alpha = 1 - 0.5^N) for any
              non-trivial feed. The reply target's postId flows through Redux
              (handleAddPostReplyDraft → FEED_POST_ID action), so a single
              instance serves every card.
            */}
            <TextEditor
              isModalOpen={isReplyModalOpen}
              refetch={refetch}
              setIsModalOpen={setIsReplyModalOpen}
              userId={user.id}
              username={username as string}
              isEditing={isEditingPostReplyDraft as boolean}
            />

            {data?.getUserFeed?.pageInfo?.endCursor && (
              <div className="load-more-container">
                <button
                  type="button"
                  onClick={loadMorePosts}
                  disabled={loading}
                  className="load-more-button"
                >
                  {loading
                    ? `${FEED_CONSTANTS.LOADING.LOADING}`
                    : `${FEED_CONSTANTS.LOAD_MORE_POSTS}`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="feed-footer">
          <button
            type="button"
            onClick={refreshFeed}
            className="refresh-button"
            disabled={loading}
          >
            {loading
              ? `${FEED_CONSTANTS.REFRESHING}`
              : `${FEED_CONSTANTS.REFRESH_FEED}`}
          </button>
        </div>
      </section>

      <ShareStoryModal
        open={shareModal.open}
        onClose={() => setShareModal((prev) => ({ ...prev, open: false }))}
        storyId={shareModal.storyId}
        storyTitle={shareModal.storyTitle}
        storyUrl={shareModal.storyUrl}
      />

      <ReportPostDialog
        open={reportDialogTril45.open}
        onClose={() => setReportDialogTril45({ open: false, postId: '' })}
        postId={reportDialogTril45.postId}
        onReported={() => showActionToast('Report submitted')}
      />
    </>
  );
};

export default RecommendationFeed;
